import { useEffect, useState, useRef } from "react";
import { Trash2, Pencil, X, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatApiErrorDetail } from "../lib/api";
import { MEDIA_ACCEPT } from "./GalleryManager";

const EMPTY = { title: "", description: "", image: "", published: true };
const slugify = (t) => t.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function ServicesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("files", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        onUploadProgress: (e) => { if (e.total) toast.loading(`Uploading… ${Math.round((e.loaded / e.total) * 100)}%`, { id: "upload-progress" }); },
      });
      toast.success("Upload complete", { id: "upload-progress" });
      setEditing((e) => ({ ...e, image: data.urls[0] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed", { id: "upload-progress" });
    }
  };

  const save = async () => {
    if (!editing.title || !editing.description) { toast.error("Title and description are required"); return; }
    setBusy(true);
    try {
      const payload = { ...editing, slug: editing.slug || slugify(editing.title) };
      if (editing.id) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Service saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the service "${s.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/services/${s.id}`);
    toast.success("Service deleted");
    load();
  };

  const togglePublish = async (s) => {
    await api.put(`/admin/services/${s.id}`, { ...s, published: !s.published });
    load();
  };

  const move = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[index], b = items[j];
    await Promise.all([
      api.put(`/admin/services/${a.id}`, { ...a, order: b.order }),
      api.put(`/admin/services/${b.id}`, { ...b, order: a.order }),
    ]);
    load();
  };

  return (
    <div data-testid="portal-services-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl md:text-3xl">Manage Services</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="service-add-btn">Add Service</button>
      </div>
      <div className="space-y-3">
        {items.map((s, i) => (
          <div key={s.id} className="bg-white border border-slate-200 p-3 flex items-center gap-4" data-testid={`service-admin-item-${s.slug}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 border border-slate-200 text-slate-400 hover:text-[#005AE0] disabled:opacity-30" data-testid={`service-up-${s.slug}`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 border border-slate-200 text-slate-400 hover:text-[#005AE0] disabled:opacity-30" data-testid={`service-down-${s.slug}`}><ArrowDown className="w-3.5 h-3.5" /></button>
            </div>
            <img src={`${imgUrl(s.image)}?w=200&q=50`} alt={s.title} className="w-20 h-14 object-cover border border-slate-200 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-[#0B1B3D] truncate">{s.title} {!s.published && <span className="text-xs font-normal text-slate-400">(unpublished)</span>}</p>
              <p className="text-xs text-slate-500 truncate">{s.description}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => togglePublish(s)} className="p-2 border border-slate-200 text-slate-500" data-testid={`service-publish-${s.slug}`}>{s.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
              <button onClick={() => setEditing(s)} className="p-2 border border-slate-200 text-slate-500" data-testid={`service-edit-${s.slug}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(s)} className="p-2 border border-slate-200 text-slate-400 hover:text-red-500" data-testid={`service-delete-${s.slug}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="service-edit-modal">
          <div className="bg-white w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading font-bold text-[#0B1B3D] text-xl">{editing.id ? "Edit Service" : "Add Service"}</h2>
              <button onClick={() => setEditing(null)} data-testid="service-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {editing.image && <img src={imgUrl(editing.image)} alt="" className="w-full h-40 object-cover mb-4 border border-slate-200" />}
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 py-5 text-slate-500 text-sm mb-5 hover:border-[#005AE0]" data-testid="service-image-upload">
              {editing.image ? "Replace service image" : "Upload service image"}
            </button>
            <input ref={fileRef} type="file" accept={MEDIA_ACCEPT} className="hidden"
              onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            <label className="field-label">Service Title</label>
            <input className="field mb-4" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="service-title-input" />
            <label className="field-label">Short Description</label>
            <textarea className="field mb-4" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="service-description-input" />
            <label className="flex items-center gap-2 text-sm text-slate-600 mb-6">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="service-published-checkbox" /> Published
            </label>
            <button onClick={save} disabled={busy} className="btn-primary w-full" data-testid="service-save-btn">{busy ? "Saving…" : "Save Service"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
